import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { findAllInclusion, syncInclusion } from "./api";
import { BOMOF_LABEL, PARENTS_INSTRUCTIONS } from "./constants";
import { employeeProfile, formatApprover, getSessionUser } from "./helpers";
import { CreateBar, EmptyState, InstructionModal, Line, PageShell, RequestCard, StatusBadge } from "./ui";

type Row = {
  referenceNumber: string;
  bomof: string;
  remarks: string;
  creationDate: string;
  status: string;
  approver: string;
};

const mapItem = (item: any): Row => ({
  referenceNumber: String(item?.ReferenceNumber ?? item?.referenceNumber ?? "-"),
  bomof: String(item?.Bomof ?? item?.bomof ?? "-"),
  remarks: String(item?.Remarks ?? item?.remarks ?? "-"),
  creationDate: String(item?.RequestCreationDate ?? "-"),
  status: String(item?.RequestStatus ?? item?.Status ?? item?.status ?? "-"),
  approver: formatApprover(
    item?.ApproverName ?? item?.approverName ?? item?.LatestApproverName,
    item?.ApproverUserID ?? item?.approverUserID
  ),
});

const InclusionParentsList = () => {
  const navigate = useNavigate();
  const routes = all_routes;
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchRequests = useCallback(async () => {
    const empId = employeeProfile(getSessionUser()).employeeNo;
    if (!empId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await findAllInclusion(empId);
      if (res.ok) setRows(res.list.map(mapItem).reverse());
      else toast.error(res.message || "Something went wrong");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSync = async () => {
    setLoading(true);
    try {
      const res = await syncInclusion();
      if (res?.success) toast.success("Sync triggered successfully");
      else toast.error(res?.message || "Something went wrong");
    } catch {
      toast.error("Something went wrong");
    } finally {
      await fetchRequests();
    }
  };

  return (
    <PageShell
      title="Inclusion and Deletion"
      subtitle="Inclusion of dependent parents including widow mother"
      onSync={handleSync}
      syncing={loading}
    >
      <CreateBar label="Create New Request" onClick={() => setShowModal(true)} />
      {loading && <div className="text-center py-4">Loading...</div>}
      {!loading &&
        rows.map((item) => (
          <RequestCard
            key={item.referenceNumber}
            reference={item.referenceNumber}
            onView={() =>
              navigate(routes.idCard.inclusionParentsView.replace(":requestID", encodeURIComponent(item.referenceNumber)))
            }
            lines={
              <>
                <Line label="Add Mother/Father" value={BOMOF_LABEL[item.bomof] || item.bomof || "-"} />
                <Line label="Request Creation Date" value={item.creationDate || "-"} />
                <Line label="Last/Latest Approver" value={item.approver} />
                <Line label="Request Status" value={<StatusBadge status={item.status} />} />
                <Line wide label="Remark" value={item.remarks || "-"} />
              </>
            }
          />
        ))}
      {!loading && rows.length === 0 && <EmptyState />}
      <InstructionModal
        open={showModal}
        text={PARENTS_INSTRUCTIONS}
        onClose={() => setShowModal(false)}
        onOk={() => {
          setShowModal(false);
          navigate(routes.idCard.createFamilyMemberRequest);
        }}
      />
    </PageShell>
  );
};

export default InclusionParentsList;
